// Reusable arcade controls: the floating hold-and-drag joystick from Boxing +
// an optional action button, with WASD/arrow + space keyboard fallback.

import { sound } from "./sound";

export class ArcadeControls {
  /** shaped -1..1 movement vector (deadzone + response curve applied) */
  x = 0;
  y = 0;
  /** action button held */
  action = false;

  private radius = 58;
  private dead = 0.16;
  private curve = 1.5;

  private root!: HTMLElement;
  private base!: HTMLDivElement;
  private nub!: HTMLDivElement;
  private btn: HTMLDivElement | null = null;
  private stickId: number | null = null;
  private origin = { x: 0, y: 0 };
  private held = new Set<string>();

  private onTouchStart = (e: TouchEvent) => {
    sound.unlock();
    for (const t of Array.from(e.changedTouches)) {
      if (this.btn?.contains(t.target as Node)) continue;
      // let taps on real controls (quit ✕, weapon chips, …) through untouched
      if ((t.target as HTMLElement)?.closest?.("button, a, input, [data-nojoy]")) continue;
      if (this.stickId !== null) continue;
      this.stickId = t.identifier;
      this.origin = { x: t.clientX, y: t.clientY };
      this.base.style.left = `${t.clientX}px`;
      this.base.style.top = `${t.clientY}px`;
      this.base.style.display = "block";
      this.setNub(0, 0);
      e.preventDefault();
    }
  };
  private onTouchMove = (e: TouchEvent) => {
    if (this.stickId === null) return;
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      let dx = t.clientX - this.origin.x;
      let dy = t.clientY - this.origin.y;
      const len = Math.hypot(dx, dy);
      if (len > this.radius) { dx = (dx / len) * this.radius; dy = (dy / len) * this.radius; }
      this.setNub(dx, dy);
      const raw = Math.min(1, len / this.radius);
      // deadzone, then a gentle response curve so light tilts stay light
      let mag = raw <= this.dead ? 0 : (raw - this.dead) / (1 - this.dead);
      mag = Math.pow(mag, this.curve);
      const ang = Math.atan2(dy, dx);
      this.x = Math.cos(ang) * mag;
      this.y = Math.sin(ang) * mag;
      e.preventDefault();
    }
  };
  private onTouchEnd = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      this.stickId = null;
      this.x = 0; this.y = 0;
      this.base.style.display = "none";
    }
  };
  private onKeyDown = (e: KeyboardEvent) => this.key(e, true);
  private onKeyUp = (e: KeyboardEvent) => this.key(e, false);

  mount(
    root: HTMLElement,
    opts: {
      actionLabel?: string; hint?: string;
      radius?: number; deadzone?: number; curve?: number;
    } = {},
  ) {
    this.root = root;
    if (opts.radius) this.radius = opts.radius;
    if (opts.deadzone != null) this.dead = opts.deadzone;
    if (opts.curve != null) this.curve = opts.curve;
    this.base = div("stick-base");
    this.nub = div("stick-nub");
    this.base.appendChild(this.nub);
    this.base.style.display = "none";
    root.appendChild(this.base);

    if (opts.actionLabel) {
      this.btn = div("punch-btn");
      this.btn.textContent = opts.actionLabel;
      root.appendChild(this.btn);
      const on = (e: Event) => { e.preventDefault(); sound.unlock(); this.btn!.classList.add("active"); this.action = true; };
      const off = (e: Event) => { e.preventDefault(); this.btn!.classList.remove("active"); this.action = false; };
      this.btn.addEventListener("touchstart", on, { passive: false });
      this.btn.addEventListener("touchend", off, { passive: false });
      this.btn.addEventListener("touchcancel", off, { passive: false });
      this.btn.addEventListener("mousedown", on);
      this.btn.addEventListener("mouseup", off);
      this.btn.addEventListener("mouseleave", off);
    }

    if (opts.hint) {
      const h = div("touch-hint");
      h.textContent = opts.hint;
      root.appendChild(h);
      setTimeout(() => h.remove(), 3500);
    }

    root.addEventListener("touchstart", this.onTouchStart, { passive: false });
    root.addEventListener("touchmove", this.onTouchMove, { passive: false });
    root.addEventListener("touchend", this.onTouchEnd);
    root.addEventListener("touchcancel", this.onTouchEnd);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  unmount() {
    this.root.removeEventListener("touchstart", this.onTouchStart);
    this.root.removeEventListener("touchmove", this.onTouchMove);
    this.root.removeEventListener("touchend", this.onTouchEnd);
    this.root.removeEventListener("touchcancel", this.onTouchEnd);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  }

  private key(e: KeyboardEvent, down: boolean) {
    const map: Record<string, string> = {
      ArrowUp: "up", KeyW: "up", ArrowDown: "down", KeyS: "down",
      ArrowLeft: "left", KeyA: "left", ArrowRight: "right", KeyD: "right",
    };
    if (map[e.code]) {
      down ? this.held.add(map[e.code]!) : this.held.delete(map[e.code]!);
      if (this.stickId === null) {
        const kx = (this.held.has("right") ? 1 : 0) - (this.held.has("left") ? 1 : 0);
        const ky = (this.held.has("down") ? 1 : 0) - (this.held.has("up") ? 1 : 0);
        const l = Math.hypot(kx, ky) || 1;
        this.x = kx / l; this.y = ky / l;
      }
      e.preventDefault();
    }
    if (e.code === "Space" || e.code === "KeyJ" || e.code === "Enter") {
      this.action = down;
      e.preventDefault();
    }
  }

  private setNub(dx: number, dy: number) {
    this.nub.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }
}

function div(cls: string) {
  const d = document.createElement("div");
  d.className = cls;
  return d;
}
